<script setup>
import { useCustomerStore } from '@/stores/customer'
import { onMounted, ref, watch } from 'vue'
import CustomerForm from '../CustomerForm.vue'
import { SwalDelete } from '@/helpers/mySwal'
import { pickContact } from '@/helpers/contacts'
import SelectAutoComplete from './SelectAutoComplete.vue'

const customerStore = useCustomerStore()
const customerId = defineModel()
const selectedCustomer = ref()
watch(selectedCustomer, (newVal) => {
  if (newVal) customerId.value = newVal.id
})
const showCustomerDialog = ref(false)

onMounted(() => {
  customerStore.getAllCustomers()
})

const insertNewCustomer = (id) => {
  showCustomerDialog.value = false
  if (id) {
    selectedCustomer.value = customerStore.customers.find((c) => c.id === id)
  }
}

function formatContactAddress(contactAddress) {
  if (!contactAddress || contactAddress.length === 0) return ''

  const addr = contactAddress[0]
  if (!addr) return ''

  // ✨ בדיקה חדשה: אם הכתובת היא מחרוזת, החזר אותה ישירות
  if (typeof addr === 'string') {
    return addr
  }

  // ✨ הטיפול הקיים באובייקט
  if (typeof addr === 'object' && addr !== null) {
    const addressParts = [...(addr.addressLines || []), addr.city, addr.region, addr.postalCode]
    return addressParts.filter(Boolean).join(', ')
  }

  // אם הטיפוס לא מוכר, החזר מחרוזת ריקה
  return ''
}

// בחירת איש קשר מהמכשיר של המשתמש
async function selectContactHandler() {
  const contact = await pickContact()
  if (!contact) return

  // קבל את הפרטים
  const name = contact.name?.[0] || ''
  const phone = contact.tel?.[0] || ''
  const email = contact.email?.[0] || ''
  const address = formatContactAddress(contact.address) || ''

  // כאן תוסיף לוגיקה לבדוק אם לקוח עם טלפון או אימייל זה כבר קיים
  const existingCustomer = customerStore.customers.find(
    (c) => c.phone === phone || c.email === email
  )
  if (existingCustomer) {
    // אם קיים, שאל את המשתמש אם לעדכן או להשתמש בקיים
    const { isConfirmed } = await SwalDelete.fire({
      title: 'לקוח כבר קיים',
      html: `
        <div class="text-right flex flex-col gap-2">
          <p>איש קשר זהה ללקוח קיים במערכת: <strong>${existingCustomer.name}</strong>.</p>
          <p>האם לעדכן את פרטי הלקוח עם המידע החדש מאנשי הקשר?</p>
          <div class="text-right bg-surface-100 p-4 rounded-lg mt-2 border">
            <p><strong>שם חדש:</strong> ${name || 'אין'}</p>
            <p><strong>טלפון חדש:</strong> ${phone || 'אין'}</p>
            <p><strong>אימייל חדש:</strong> ${email || 'אין'}</p>
            <p><strong>כתובת חדשה:</strong> ${address || 'אין'}</p>
          </div>
        </div>`,
      confirmButtonText: 'כן, עדכן!',
      cancelButtonText: 'לא, השתמש בקיים'
    })

    if (isConfirmed) {
      const updatePayload = {
        id: existingCustomer.id,
        name: name || existingCustomer.name,
        phone: phone || existingCustomer.phone,
        email: email || existingCustomer.email,
        address: address || existingCustomer.address
      }
      const id = await customerStore.updateCustomer(updatePayload)
      if (!id) return
      selectedCustomer.value = customerStore.customers.find((c) => c.id === id)
    } else {
      // השתמש בקיים
      selectedCustomer.value = existingCustomer
    }
  } else {
    // אם לא קיים, פשוט מלא את הטופס
    const id = await customerStore.createCustomer({ name, phone, email, address })
    if (!id) return
    selectedCustomer.value = customerStore.customers.find((c) => c.id === id)
  }
}

const inputValue = ref('')
</script>

<template>
  <SelectAutoComplete
    v-model="customerId"
    :options="customerStore.customers"
    option-label="name"
    option-value="id"
    placeholder="הקלד או בחר לקוח"
    fluid
    dropdown
    force-selection
    input-id="customerId"
    :virtual-scroller-options="{ itemSize: 38 }"
    append-to="self"
    @change="({ value }) => (value ? (inputValue = value) : (inputValue = inputValue))"
  >
    <template #footer>
      <div class="flex flex-col gap-2 p-2">
        <Button
          label="לקוח חדש"
          @click="showCustomerDialog = true"
          icon="pi pi-plus"
          fluid
          severity="secondary"
        />
        <Button
          label="בחר מאנשי קשר"
          @click="selectContactHandler"
          icon="pi pi-users"
          fluid
          severity="secondary"
        />
      </div>
    </template>
  </SelectAutoComplete>

  <Dialog
    v-model:visible="showCustomerDialog"
    :style="{ width: '350px' }"
    header="הוספת לקוח"
    modal
    @update:visible="(value) => console.log('visible', value)"
  >
    <CustomerForm :customer-name="inputValue" @close="insertNewCustomer" />
  </Dialog>
</template>

<style>
.p-virtualscroller-content {
  right: 0;
}
</style>
